import { Building2, HardHat, Home as HomeIcon, Package, Snowflake } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import CtaBanner from "@/components/CtaBanner";

const WHAT_WE_DO = [
  { icon: Package, title: "Wholesale air conditioning supply." },
  { icon: HomeIcon, title: "Residential cooling equipment." },
  { icon: Building2, title: "Commercial cooling solutions." },
  { icon: Snowflake, title: "Refrigerated air conditioning systems." },
  { icon: HardHat, title: "Supply support for builders and property projects." },
];

export default function About() {
  return (
    <>
      <Seo
        title="About GSMG INVESTMENTS | Wholesale Cooling Equipment Supply"
        description="GSMG INVESTMENTS focuses on the wholesale supply of air conditioning and refrigerated cooling solutions for residential, commercial and builder projects."
      />

      {/* Page header */}
      <section className="grain relative overflow-hidden bg-ink pb-16 pt-32 text-white sm:pt-36 lg:pb-20 lg:pt-44">
        <svg className="airflow absolute inset-x-0 top-0 h-full w-full opacity-25" viewBox="0 0 1440 600" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <path d="M-60 200 C 380 90, 960 300, 1520 150" stroke="#38BDF8" strokeWidth="1.5" fill="none" />
          <path d="M-60 430 C 420 310, 980 500, 1520 360" stroke="#00A8E8" strokeWidth="1.5" fill="none" />
        </svg>
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-sky-300">About us</p>
          <h1 data-testid="about-heading" className="mt-5 max-w-3xl font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            About GSMG INVESTMENTS
          </h1>
          <Reveal delay={0.2} y={20}>
            <p className="mt-7 max-w-3xl text-base leading-relaxed text-slate-300 sm:text-lg">
              GSMG INVESTMENTS focuses on the wholesale supply of air conditioning and refrigerated cooling solutions for residential and commercial applications.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Introduction */}
      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 lg:grid-cols-12 lg:px-8">
          <Reveal className="lg:col-span-7">
            <div className="space-y-6 text-base leading-relaxed text-slate-600 sm:text-lg">
              <p>
                The business supports builders and other industry professionals by supplying cooling equipment suited to a range of project requirements, from residential construction to commercial property applications.
              </p>
              <p>
                GSMG INVESTMENTS aims to make the equipment supply process straightforward by understanding each project's requirements and helping customers identify suitable solutions for their needs.
              </p>
              <p>
                With a focus on residential cooling, commercial air conditioning and builder supply, GSMG INVESTMENTS serves customers looking for practical equipment supply solutions for their projects.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-lg shadow-navy/5">
              <img
                src="https://images.unsplash.com/photo-1617761141732-d481912af1a9?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200"
                alt="Commercial building project representing builder supply work"
                loading="lazy"
                className="h-72 w-full object-cover lg:h-full lg:min-h-[24rem]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* What We Do */}
      <section className="bg-mist py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">Our focus</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">What We Do</h2>
          </Reveal>
          <div className="mt-12" data-testid="what-we-do-list">
            {WHAT_WE_DO.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.07} y={16}>
                <div className="group flex items-center gap-5 border-t border-slate-200 py-6 transition-colors last:border-b hover:bg-white sm:gap-8 sm:px-4">
                  <span className="font-mono text-sm tracking-[0.2em] text-brand">0{i + 1}</span>
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-navy text-white transition-colors group-hover:bg-brand" aria-hidden="true">
                    <item.icon size={20} />
                  </span>
                  <p className="font-display text-lg font-semibold text-ink sm:text-xl">{item.title}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner
        title="Let's Discuss Your Project"
        text="Contact GSMG INVESTMENTS to discuss your residential, commercial or builder supply requirements."
        testId="about-final-cta-btn"
      />
    </>
  );
}
